/**
 * Reproductor de la voz de Androcles (audio TTS de las respuestas).
 *
 * El backend sintetiza la respuesta FRASE A FRASE y manda cada WAV por el
 * socket (o el parte llega de una via REST). Este modulo los ENCOLA y los
 * reproduce en orden, sin solaparse: la frase dos no empieza hasta que acaba
 * la uno. Asi la conversacion suena natural aunque las frases lleguen a rachas.
 *
 * Reglas para el usuario de 88 anos:
 *  - Un solo hilo de voz: nunca dos frases a la vez encima.
 *  - Silenciar es inmediato: corta lo que suena y vacia la cola. Las respuestas
 *    siguen viendose en texto; callar la voz no apaga el kiosco.
 *  - Un WAV ilegible se salta, no atasca la cola.
 *
 * Reproduccion via un elemento `<audio>` reutilizable (canal de medios), no
 * via WebAudio: en iOS el interruptor de silencio del telefono calla WebAudio
 * pero no el `<audio>`. Ver el bloque de reproduccion mas abajo.
 */

/** Avisos del reproductor hacia la interfaz (indicador de "hablando"). */
export interface OpcionesReproductor {
  /** Se llama al arrancar la voz (paso de callado a hablando). */
  alHablar?: () => void;
  /** Se llama cuando la cola se vacia y deja de sonar. */
  alCallar?: () => void;
}

/**
 * Observadores del audio que se va a reproducir.
 *
 * Sirve para DERIVAR el mismo WAV a otro consumidor (el avatar, que lo usa
 * para el lip-sync) SIN tocar la reproduccion. Se llama con el WAV justo al
 * encolarlo, en paralelo a que suene. Es aparte de `OpcionesReproductor`
 * (avisos de UI) porque no gobierna la voz: si el observador falla, la voz
 * no se entera.
 */
type ObservadorAudio = (wav: ArrayBuffer) => void;
const _observadores: Set<ObservadorAudio> = new Set();

/**
 * Registra un observador del audio encolado. Devuelve la funcion para darse
 * de baja. El WAV llega como una COPIA: el observador puede consumirlo
 * (p. ej. `decodeAudioData`) sin invalidar el que se reproduce.
 */
export function observarAudio(observador: ObservadorAudio): () => void {
  _observadores.add(observador);
  return () => {
    _observadores.delete(observador);
  };
}

/** Notifica a los observadores, aislando cualquier fallo suyo de la voz. */
function _notificarObservadores(wav: ArrayBuffer): void {
  if (_observadores.size === 0) return;
  for (const observador of _observadores) {
    try {
      observador(wav.slice(0));
    } catch {
      // Un observador roto (p. ej. el socket del avatar caido) jamas debe
      // afectar a la reproduccion de voz. Se ignora en silencio.
    }
  }
}

/**
 * Reproduccion via un elemento `<audio>` reutilizable, NO via WebAudio.
 *
 * MOTIVO (reporte del jefe, 25/07/2026: no se oye nada en el iPhone): en iOS,
 * el audio de `AudioContext` (WebAudio) queda SILENCIADO por el interruptor
 * fisico de silencio del telefono, mientras que un `<audio>` suena por el
 * canal de medios (como un video) y ese interruptor no lo calla. Para un
 * kiosco de una persona de 88 anos no se puede depender de que el interruptor
 * lateral este siempre bien puesto, asi que la voz va por `<audio>`.
 *
 * El WAV sigue derivandose a los observadores (el avatar) por separado: eso usa
 * los bytes, no el modo de reproduccion.
 */
let _audio: HTMLAudioElement | null = null;
let _cola: string[] = []; // URLs de blob pendientes
let _urlActual: string | null = null;
/** true mientras el bucle de reproduccion esta activo. */
let _procesando = false;
/** true entre el primer sonido y el fin de la cola; gobierna los avisos. */
let _hablando = false;
/** true si el jefe ha pedido silencio: nada suena hasta que lo quite. */
let _silenciado = false;
/** Silenciar o parar lo incrementa; una reproduccion vieja se descarta. */
let _generacion = 0;

let _alHablar: (() => void) | null = null;
let _alCallar: (() => void) | null = null;

// --------------------------------------------------------------------------
// Modo sincronizado (acumular la respuesta ENTERA antes de reproducirla)
// --------------------------------------------------------------------------
//
// PROBLEMA que resuelve: MuseTalk genera los frames de lip-sync ~2.9x MAS
// LENTO que tiempo real. Reproduciendo FRASE A FRASE, entre frase y frase la
// cara se congela varios segundos mientras se genera la siguiente, y el
// encadenado tenia bugs (repetia frases). En esta GPU no se puede streamear en
// tiempo real.
//
// SOLUCION (aprobada por Ricardo): en modo sincronizado la voz NO suena al
// encolarse. Todos los WAVs de la respuesta se ACUMULAN aqui; el avatar, cuando
// la respuesta esta COMPLETA y sus frames pre-decodificados, ordena reproducir
// la respuesta ENTERA de una vez con `reproducirRespuestaAcumulada`: los WAVs
// suenan back-to-back (uno tras otro sin huecos) y los frames se pintan a fps
// constante, todo sincronizado y SIN congelados intermedios. El precio es una
// espera mas larga mientras "piensa" (cara en reposo/pensando), a cambio de que
// cuando hable sea suave.
//
// SEGURIDAD (la voz NUNCA se queda muda): el avatar vigila la respuesta y, si
// tarda demasiado o degrada a mitad, ordena reproducir los WAVs acumulados
// IGUAL (sin esperar a los frames). Ademas, al DESACTIVAR el modo sincronizado
// se vuelca lo acumulado (ver `modoSincronizado`) y al parar/silenciar se
// descarta (ver `pararVoz`). La voz es el minimo garantizado.

/** true si la voz debe acumular la respuesta entera antes de sonar (modo sincronizado). */
let _sincronizado = false;

/**
 * WAVs de la respuesta EN CURSO, en orden de llegada (el MISMO en que el
 * backend sintetiza las frases). En modo sincronizado no suenan al encolarse:
 * se acumulan aqui hasta que el avatar ordena reproducir la respuesta entera.
 */
let _respuestaWavs: ArrayBuffer[] = [];

/** Registra los avisos de "hablando"/"callado". Se llama una vez al montar. */
export function configurarReproductor(opciones: OpcionesReproductor): void {
  _alHablar = opciones.alHablar ?? null;
  _alCallar = opciones.alCallar ?? null;
}

/** Elemento `<audio>` perezoso y reutilizable. */
function _elemento(): HTMLAudioElement {
  if (!_audio) {
    _audio = new Audio();
    _audio.preload = 'auto';
    // Reproduccion en linea (iOS no abre pantalla completa) y por el canal de
    // medios, que el interruptor de silencio del iPhone no calla.
    _audio.setAttribute('playsinline', '');
  }
  return _audio;
}

/**
 * Desbloquea el audio DENTRO del gesto del usuario.
 *
 * iOS solo deja sonar un `<audio>` si su primer `play()` ocurre dentro del
 * manejador de un toque. La reproduccion real llega despues de un `await` (la
 * sintesis viaja por red), cuando el gesto ya esta "gastado". La cura: llamar
 * a esto SIN await desde el propio `onClick` del microfono y de "Leemelo",
 * antes de cualquier trabajo asincrono. Suena un WAV mudo minimo y con eso el
 * elemento queda autorizado para las reproducciones posteriores.
 */
export function desbloquear(): void {
  try {
    const el = _elemento();
    // WAV mudo de 0 datos: termina al instante, no hay que pausarlo (pausar
    // aqui podria detener por error la reproduccion real que venga justo despues,
    // porque comparten el mismo elemento).
    el.src =
      'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=';
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        /* si se niega, la reproduccion real lo reintenta */
      });
    }
  } catch {
    // Si el navegador se niega, la reproduccion posterior lo reintentara.
  }
}

/** true si el jefe tiene la voz silenciada. */
export function estaSilenciado(): boolean {
  return _silenciado;
}

/** true si en este momento suena la voz. */
export function estaHablando(): boolean {
  return _hablando;
}

/**
 * Activa o quita el silencio de la voz.
 *
 * Al silenciar se corta lo que suena y se vacia la cola: el jefe no quiere
 * seguir oyendo el resto del parrafo. Las respuestas siguen en texto.
 */
export function ponerSilencio(valor: boolean): void {
  _silenciado = valor;
  if (valor) pararVoz();
}

/**
 * Encola un WAV para reproducirlo cuando le toque.
 *
 * En modo NO sincronizado (por defecto): suena en cuanto le toca en la cola.
 * En modo sincronizado: NO suena ahora; se ACUMULA en la respuesta en curso
 * hasta que el avatar ordene reproducirla entera (`reproducirRespuestaAcumulada`)
 * o la vuelque la seguridad. En ambos casos el WAV se deriva al avatar
 * (observadores) primero, para que este pueda generar el lip-sync.
 *
 * Args:
 *     wav: audio WAV completo (una frase sintetizada).
 */
export function encolarAudio(wav: ArrayBuffer): void {
  if (_silenciado || wav.byteLength === 0) return;
  // El avatar recibe el MISMO WAV para el lip-sync. Se hace SIEMPRE y lo
  // primero: es lo que arranca la generacion del lote en el avatar.
  _notificarObservadores(wav);
  if (_sincronizado) {
    // Modo sincronizado: acumular; la respuesta entera sonara despues, de golpe.
    _respuestaWavs.push(wav);
    return;
  }
  // Modo normal (avatar ausente/degradado): sonar en cuanto toque.
  _reproducirYa(wav);
}

/** Mete un WAV en la cola de reproduccion y arranca el bombeo si hace falta. */
function _reproducirYa(wav: ArrayBuffer): void {
  if (_silenciado || wav.byteLength === 0) return;
  const url = URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }));
  _cola.push(url);
  if (!_procesando) void _bombear();
}

/**
 * Activa o desactiva el modo sincronizado.
 *
 * El avatar lo activa cuando su WebSocket esta abierto y NO degradado, y lo
 * desactiva al degradar/cerrar/desmontar (asi la voz vuelve a ser inmediata).
 *
 * Al DESACTIVAR se VUELCA la respuesta acumulada reproduciendola: no se deja
 * ningun WAV retenido sin sonar (cubre "el avatar degrada a mitad").
 *
 * Args:
 *     activo: true para acumular la respuesta entera antes de sonar.
 */
export function modoSincronizado(activo: boolean): void {
  if (_sincronizado === activo) return;
  _sincronizado = activo;
  if (!activo) {
    // Volcar lo acumulado: la voz no se pierde al salir del modo sync.
    reproducirRespuestaAcumulada();
  }
}

/**
 * Reproduce la respuesta acumulada ENTERA, back-to-back y sin huecos.
 *
 * La ordena el avatar cuando la respuesta esta COMPLETA y sus frames ya estan
 * pre-decodificados (para que voz y video arranquen juntos), o cuando la
 * seguridad decide que hay que soltar la voz sin esperar mas a los frames.
 *
 * Vacia el buffer: la siguiente respuesta empieza limpia y ningun WAV se
 * reproduce dos veces. Reproduce por el mismo camino `<audio>` (fix de iOS): el
 * bucle `_bombear` los suena uno tras otro (al 'ended' de uno, el siguiente).
 *
 * Returns:
 *     true si habia algo que reproducir; false si el buffer estaba vacio.
 */
export function reproducirRespuestaAcumulada(): boolean {
  const wavs = _respuestaWavs;
  _respuestaWavs = [];
  if (wavs.length === 0) return false;
  for (const wav of wavs) _reproducirYa(wav);
  return true;
}

/**
 * Descarta la respuesta acumulada SIN reproducirla.
 *
 * La usa el avatar al empezar una respuesta nueva (para no arrastrar restos de
 * una interrumpida) y `pararVoz` al cortar la voz.
 */
export function descartarRespuestaAcumulada(): void {
  _respuestaWavs = [];
}

/** Bucle que reproduce la cola frase a frase hasta agotarla. */
async function _bombear(): Promise<void> {
  _procesando = true;
  const generacion = _generacion;
  while (_cola.length > 0 && !_silenciado && generacion === _generacion) {
    const url = _cola.shift()!;
    if (!_hablando) {
      _hablando = true;
      _alHablar?.();
    }
    await _reproducirUno(url, generacion);
  }
  _procesando = false;
  if (_hablando && (generacion === _generacion || _cola.length === 0)) {
    _hablando = false;
    _alCallar?.();
  }
}

/**
 * Reproduce un unico blob y resuelve cuando termina (o si se descarta).
 *
 * Args:
 *     url: blob URL del WAV a sonar.
 *     generacion: generacion con la que se encolo; si cambia, se descarta.
 */
function _reproducirUno(url: string, generacion: number): Promise<void> {
  return new Promise((resolver) => {
    if (_silenciado || generacion !== _generacion) {
      URL.revokeObjectURL(url);
      resolver();
      return;
    }
    const el = _elemento();
    _urlActual = url;

    const limpiar = () => {
      el.onended = null;
      el.onerror = null;
      if (_urlActual === url) _urlActual = null;
      URL.revokeObjectURL(url);
      resolver();
    };
    el.onended = limpiar;
    el.onerror = limpiar; // Un WAV ilegible se salta, no atasca la cola.

    el.src = url;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // El navegador puede negarse sin gesto; el jefe lo provoca al pulsar.
        limpiar();
      });
    }
  });
}

/**
 * Corta la voz en seco y vacia la cola.
 *
 * Se usa al silenciar y cuando el jefe vuelve a hablar (no tiene sentido que
 * Androcles siga hablando encima). No apaga el kiosco ni la conversacion.
 */
export function pararVoz(): void {
  _generacion += 1; // invalida reproducciones en vuelo
  // Descartar tambien la respuesta acumulada en modo sync (parar = no seguir):
  // no debe resucitar la voz de una respuesta que el jefe ha interrumpido.
  descartarRespuestaAcumulada();
  for (const url of _cola) URL.revokeObjectURL(url);
  _cola = [];
  if (_audio) {
    try {
      _audio.pause();
      _audio.removeAttribute('src');
    } catch {
      // Parar un elemento ya detenido es inofensivo.
    }
  }
  if (_urlActual) {
    URL.revokeObjectURL(_urlActual);
    _urlActual = null;
  }
  if (_hablando) {
    _hablando = false;
    _alCallar?.();
  }
}
